/**
 * Cloudflare tag purge — which pages the Worker answers, and which cache tags
 * clear them.
 *
 * `www` traffic for a migrated path is answered by the Cloudflare Worker (Cache
 * API + KV warm store) and never reaches Fastly, so a Fastly URL purge of such a
 * page is a no-op. The Worker's cache keys are namespaced, so a URL purge cannot
 * reach them either; a purge by TAG can (`contracts/cache-tags.md`, point 3).
 *
 * This file is the ONE list of Worker-owned path families for the admin panel.
 * `cloudflare-purge.test.ts` pins it to the `routes` of the `production` env in
 * `apps/website/wrangler.jsonc`, so it cannot drift from the edge.
 *
 * The tag strings are a cross-repo contract (`contracts/cache-tags.md`, "Parameterized
 * tags"); the test also pins them to `cache-policy.json`.
 */

import type { AssetTypeKey, PurgeCdn } from "./data-purge.js";

/** Asset class ids as the website's `AssetClassId` names them (the tag segment). */
export type WorkerAssetClass =
  | "stock"
  | "etf"
  | "fund"
  | "crypto"
  | "index"
  | "fx"
  | "commodity"
  | "private-company";

export interface WorkerFamilyDef {
  /**
   * The path half of the Cloudflare route pattern, exactly as `wrangler.jsonc`
   * declares it (`/mutual-funds*`). Always ends in `*` (TASK-0436 incident 4).
   */
  pattern: string;
  /**
   * `live`    — bound on `www` today; MUST be in `env.production.routes`.
   * `planned` — a family of the next flips. Purged ahead of the flip so the
   *             operator's purge works the moment the route binds (a tag purge of
   *             a page the Worker does not serve yet clears nothing and is harmless).
   *             It MAY already be in the routes; it need not be.
   */
  status: "live" | "planned";
  /**
   * Asset class whose `asset:<class>:<ticker>` tags this prefix emits. Absent for a
   * page family with no per-asset pages (a tool): its `pages` tags are all it has.
   */
  assetClass?: WorkerAssetClass;
  /** The `/<base>/:ticker[/:tab]` prefix the per-asset pages live under. */
  assetBase?: string;
  /** Listing / hub pages: exact path → the family tag(s) the page carries. */
  pages: Readonly<Record<string, FamilyTags>>;
  /**
   * Listing pages with a sub-path: path prefix → the family tag(s). Longest wins.
   * Several tags when one prefix holds two templates (`/experts/insiders/us` is a
   * list, `/experts/insiders/elon-musk` a profile): over-purging is safe.
   */
  pagePrefixes?: Readonly<Record<string, FamilyTags>>;
}

/** One cache tag, or several. */
export type FamilyTags = string | readonly string[];

const tagList = (tags: FamilyTags): string[] => (typeof tags === "string" ? [tags] : [...tags]);

/** Every tag a family's `pages` / `pagePrefixes` name (the cross-repo contract test). */
export function familyTags(family: WorkerFamilyDef): string[] {
  return [
    ...Object.values(family.pages).flatMap(tagList),
    ...Object.values(family.pagePrefixes ?? {}).flatMap(tagList),
  ];
}

/** Worker-owned www path families, in rollout order. */
export const WORKER_FAMILIES: readonly WorkerFamilyDef[] = [
  {
    pattern: "/mutual-funds*",
    status: "live",
    assetClass: "fund",
    assetBase: "/mutual-funds",
    pages: { "/mutual-funds": "asset-center:mutual-funds" },
  },
  {
    pattern: "/cryptocurrency*",
    status: "planned",
    assetClass: "crypto",
    assetBase: "/cryptocurrency",
    pages: { "/cryptocurrency": "asset-center:crypto" },
  },
  {
    pattern: "/currencies*",
    status: "planned",
    assetClass: "fx",
    assetBase: "/currencies",
    pages: { "/currencies": "asset-center:currencies" },
  },
  {
    pattern: "/commodities*",
    status: "planned",
    assetClass: "commodity",
    assetBase: "/commodities",
    pages: { "/commodities": "asset-center:commodities" },
  },
  {
    pattern: "/index*",
    status: "planned",
    assetClass: "index",
    assetBase: "/index",
    pages: { "/index": "asset-center:index" },
  },
  {
    pattern: "/private-companies*",
    status: "planned",
    assetClass: "private-company",
    assetBase: "/private-companies",
    pages: {
      "/private-companies": "private-companies:center",
      "/private-companies/investors": "private-companies:investors",
      // TR-26697: the sector directory (the bare path; the per-sector pages are the prefix below).
      "/private-companies/sector": "private-companies:sectors",
    },
    pagePrefixes: {
      "/private-companies/investor/": "private-companies:investor",
      "/private-companies/sector/": "private-companies:sector",
    },
  },
  {
    pattern: "/etf*",
    status: "planned",
    assetClass: "etf",
    assetBase: "/etf",
    pages: { "/etfs": "asset-center:etfs" },
    pagePrefixes: { "/etfs/": "asset-center:etfs" },
  },
  {
    // TR-26652: one page, no legacy twin. Its document embeds the SPDR ETFs' EOD facts.
    pattern: "/tools/sector-income-tool*",
    status: "live",
    pages: { "/tools/sector-income-tool": "tools:sector-income" },
  },
  {
    // TASK-0514: the screener family. Bare paths are exact pages; a market or
    // page segment rides the prefix with the trailing slash.
    pattern: "/screener*",
    // Stays "planned" until the flip PR binds `www.tipranks.com/screener*` (the
    // routes test requires every "live" family to be bound); flip it there (B1).
    status: "planned",
    pages: {
      "/screener": "screener:hub",
      "/screener/stocks": "screener:stocks",
      "/screener/etfs": "screener:etfs",
      "/screener/etfs/by-aum": "asset-center:etfs",
      "/screener/etfs/top-smart-score": "screener:top-etf-smart-score",
      "/screener/etfs/top-upside-potential": "screener:top-etf-upside",
      "/screener/analysts-top-stocks": "screener:analysts-top-stocks",
      "/screener/top-smart-score-stocks": "screener:top-smart-score-stocks",
      "/screener/top-stocks-by-ai": "screener:top-stocks-by-ai",
      "/screener/trending-stocks": "screener:trending",
      "/screener/trending-stocks/most-followed": "screener:trending-most-followed",
      "/screener/trending-stocks/unusual-trading-volume": "screener:trending-unusual-volume",
      "/screener/penny-stocks": "screener:penny-stocks",
      "/screener/stock-ratings": "screener:stock-ratings",
      "/screener/technical-analysis": "screener:technical-analysis",
      "/screener/top-insider-trading-stocks": "screener:top-insider-trading-stocks",
      "/screener/daily-insider-trading-tracker": "screener:insider-tracker",
      "/screener/most-shorted-stocks": "screener:most-shorted-stocks",
      "/screener/private-companies": "private-companies:directory",
      // The ETF movers: ten literal slugs under /screener/etfs/, each its own page.
      "/screener/etfs/most-active-etfs": "screener:etfs-on-the-move",
      "/screener/etfs/top-gainers-today": "screener:etfs-on-the-move",
      "/screener/etfs/top-losers-today": "screener:etfs-on-the-move",
      "/screener/etfs/52-week-high": "screener:etfs-on-the-move",
      "/screener/etfs/52-week-low": "screener:etfs-on-the-move",
      "/screener/etfs/unusual-volume": "screener:etfs-on-the-move",
      "/screener/etfs/pre-market-gainers": "screener:etfs-on-the-move",
      "/screener/etfs/after-hours-gainers": "screener:etfs-on-the-move",
      "/screener/etfs/after-hours-losers": "screener:etfs-on-the-move",
      "/screener/etfs/pre-market-losers": "screener:etfs-on-the-move",
      // The tr-picks portfolios: static documents (no data in the HTML).
      "/screener/smart-investor": "page:static",
      "/screener/smart-growth": "page:static",
      "/screener/gmhikaru": "page:static",
    },
    pagePrefixes: {
      "/screener/stocks/": "screener:stocks",
      "/screener/etfs/": "screener:etfs",
      "/screener/etfs/top-smart-score/": "screener:top-etf-smart-score",
      "/screener/etfs/top-upside-potential/": "screener:top-etf-upside",
      "/screener/analysts-top-stocks/": "screener:analysts-top-stocks",
      "/screener/top-smart-score-stocks/": "screener:top-smart-score-stocks",
      "/screener/top-stocks-by-ai/": "screener:top-stocks-by-ai",
      "/screener/trending-stocks/": "screener:trending",
      "/screener/stock-ratings/": "screener:stock-ratings",
      "/screener/technical-analysis/": "screener:technical-analysis",
      "/screener/top-insider-trading-stocks/": "screener:top-insider-trading-stocks",
      "/screener/daily-insider-trading-tracker/": "screener:insider-tracker",
      "/screener/private-companies/": "private-companies:directory",
      "/screener/most-shorted-stocks/": "screener:most-shorted-stocks",
      "/screener/penny-stocks/": "screener:penny-stocks",
    },
  },
  {
    // TASK-0070 (first news wave, 2026-10-25): the portal. An article is
    // /news/<slug> or /news/<category>/<slug>, so the bare /news/ prefix is the
    // article tag and the longer list prefixes win over it. Paths legacy keeps
    // under the prefix (/news-*, /newsletter*, /news/*-rss) are forwarded to the
    // origin (website lib/legacy-origin-paths.ts); purging them clears nothing.
    pattern: "/news*",
    status: "live",
    pages: { "/news": "news:main", "/news/search": "news:search" },
    pagePrefixes: {
      "/news/": "news:article",
      "/news/topic/": "news:topic",
      "/news/category/": "news:topic",
      "/news/author/": "news:author",
    },
  },
  {
    // TASK-0045 wave E3 (2026-10-25): bare list, market lists and profiles.
    pattern: "/experts/insiders*",
    status: "planned",
    pages: { "/experts/insiders": "experts:list" },
    pagePrefixes: { "/experts/insiders/": ["experts:list", "expert:profile"] },
  },
  {
    pattern: "/experts/hedge-funds*",
    status: "live",
    pages: { "/experts/hedge-funds": "experts:list" },
    pagePrefixes: { "/experts/hedge-funds/": ["experts:list", "expert:profile"] },
  },
  {
    pattern: "/experts/bloggers*",
    status: "planned",
    pages: { "/experts/bloggers": "experts:list" },
    pagePrefixes: { "/experts/bloggers/": ["experts:list", "expert:profile"] },
  },
  {
    // The rest of the experts tree (2026-11-01). ONE entry, like the one
    // `www.tipranks.com/experts*` route that flips it: no legacy page stays
    // under /experts. Listed after the three sub-families of the 10-25 wave,
    // which match first and keep their own entries (and routes) until then.
    pattern: "/experts*",
    status: "live",
    pages: {
      "/experts": "page:static",
      "/experts/how-experts-ranked": "page:static",
      "/experts/firms": "experts:firms",
      "/experts/analysts": "experts:list",
      "/experts/ai-analysts": "experts:list",
      "/experts/investors": "experts:list",
    },
    pagePrefixes: {
      // `/experts/:slug[/:marketCountry]` lists and `/experts/:type/:slug`
      // profiles share the prefix; the longer prefixes below win over it.
      "/experts/": ["experts:list", "expert:profile"],
      "/experts/analysts/": ["experts:list", "expert:profile"],
      "/experts/ai-analysts/": "experts:list",
      "/experts/politicians/": "expert:profile",
      "/experts/firms/": "experts:firm",
      "/experts/investors/": "portfolio:public",
    },
  },
  {
    // The stock pages (2026-11-01): `/stocks` is the center, everything under
    // `/stocks/<ticker>` is one ticker's tag. `/stocks-to-buy` and
    // `/stocks/top-rated` are 301s; purging them clears nothing that matters.
    pattern: "/stocks*",
    status: "live",
    assetClass: "stock",
    assetBase: "/stocks",
    pages: { "/stocks": "asset-center:stocks" },
  },
  {
    // TASK-0514: the rest of /tools. Listed after the sector-income entry, which
    // keeps its own tag (the first matching family wins).
    pattern: "/tools*",
    status: "planned",
    pages: {
      "/tools": "page:static",
      "/tools/dividend-yield-calculator": "tools:dividend-yield",
      "/tools/options-profit-calculator": "page:static",
      "/tools/dividend-calculator": "tools:dividend-calculator",
      "/tools/dividend-returns-comparison": "tools:dividend-comparison",
      "/tools/etf-stock-exposure-tool": "tools:etf-exposure",
    },
    pagePrefixes: {
      "/tools/dividend-returns-comparison/": "tools:dividend-comparison",
      "/tools/etf-stock-exposure-tool/": "tools:etf-exposure",
    },
  },
  // ── Group 1 cutover (TASK-0538, 2026-11-08). All "planned" until the flip PR
  // binds the routes (wrangler draft in the B1 PR body) and flips them to "live".
  // "/" is an EXACT route: matchesPattern treats a pattern without a trailing `*`
  // as an exact path.
  { pattern: "/", status: "planned", pages: { "/": "page:home" } },
  { pattern: "/plans*", status: "planned", pages: { "/plans": "page:plans" } },
  {
    pattern: "/glossary*",
    status: "planned",
    pages: { "/glossary": "content:glossary" },
    pagePrefixes: { "/glossary/": "content:glossary" },
  },
  { pattern: "/video/*", status: "planned", pages: {}, pagePrefixes: { "/video/": "page:video" } },
  {
    pattern: "/calendars*",
    status: "planned",
    pages: {
      "/calendars": "calendar:hub",
      "/calendars/earnings": "calendar:earnings",
      "/calendars/earnings-today": "calendar:earnings-today",
      "/calendars/dividends": "calendar:dividends",
      "/calendars/ipo": "calendar:ipo",
      "/calendars/economic": "calendar:economic",
      "/calendars/fda": "calendar:fda",
      "/calendars/stock-buybacks": "calendar:buybacks",
      "/calendars/stock-splits": "calendar:stock-splits",
      "/calendars/market-holidays": "calendar:market-holidays",
    },
    pagePrefixes: {
      "/calendars/earnings/": "calendar:earnings",
      "/calendars/dividends/": "calendar:dividends",
      "/calendars/ipo/": "calendar:ipo",
      "/calendars/economic/": "calendar:economic",
      "/calendars/stock-splits/": "calendar:stock-splits",
      "/calendars/market-holidays/": "calendar:market-holidays",
    },
  },
  {
    pattern: "/economic-indicators*",
    status: "planned",
    pages: { "/economic-indicators": "economic:indicators" },
    pagePrefixes: { "/economic-indicators/": "economic:indicators" },
  },
  {
    pattern: "/dividends*",
    status: "planned",
    pages: {
      "/dividends": "screener:dividends-hub",
      "/dividends/dividend-aristocrats": "screener:dividend-aristocrats",
      "/dividends/best-dividend-stocks": "screener:best-dividend-stocks",
      "/dividends/best-high-dividend-stocks": "screener:best-high-dividend-stocks",
    },
    pagePrefixes: {
      "/dividends/best-dividend-stocks/": "screener:best-dividend-stocks",
      "/dividends/best-high-dividend-stocks/": "screener:best-high-dividend-stocks",
    },
  },
  { pattern: "/markets*", status: "planned", pages: {}, pagePrefixes: { "/markets/": "markets:movers" } },
  {
    pattern: "/options*",
    status: "planned",
    pages: { "/options": "options:hub" },
    pagePrefixes: { "/options/": "options:screener" },
  },
  {
    pattern: "/compare-stocks*",
    status: "planned",
    pages: { "/compare-stocks": "compare:stocks" },
    pagePrefixes: { "/compare-stocks/": "compare:stocks" },
  },
  {
    pattern: "/compare-etfs*",
    status: "planned",
    pages: { "/compare-etfs": "compare:etfs" },
    pagePrefixes: { "/compare-etfs/": "compare:etfs" },
  },
  {
    pattern: "/largest-companies*",
    status: "planned",
    pages: { "/largest-companies": "screener:largest-companies" },
    pagePrefixes: { "/largest-companies/": "screener:largest-companies" },
  },
  {
    pattern: "/class-action-lawsuits*",
    status: "planned",
    pages: { "/class-action-lawsuits": "research:class-action-lawsuits" },
  },
  {
    pattern: "/trump-dashboard*",
    status: "planned",
    pages: { "/trump-dashboard": "research:trump-dashboard" },
  },
];

/** Path patterns of the Worker-owned families (for the wrangler.jsonc pin). */
export function workerFamilyPatterns(): string[] {
  return WORKER_FAMILIES.map((f) => f.pattern);
}

function matchesPattern(pattern: string, path: string): boolean {
  // Cloudflare route semantics: `*` matches any run of characters. A pattern
  // ending in a single `*` is a prefix test; one without (the home page, `/`) is
  // an exact path. (`"/".slice(0, -1)` is "", which used to match every path.)
  return pattern.endsWith("*") ? path.startsWith(pattern.slice(0, -1)) : path === pattern;
}

/** The Worker family that answers `path`, if any. */
export function workerFamilyForPath(path: string): WorkerFamilyDef | undefined {
  return WORKER_FAMILIES.find((f) => matchesPattern(f.pattern, path));
}

/**
 * Lower-case, query-free path with no trailing slash — the form the tag
 * grammar is built from (`contracts/cache-tags.md`: ticker lower-cased).
 */
function normalizePath(path: string): string {
  const cut = path.search(/[?#]/);
  const bare = (cut === -1 ? path : path.slice(0, cut)).toLowerCase();
  return bare.length > 1 ? bare.replace(/\/+$/, "") : bare;
}

/** `payload.json` siblings are legacy server-state objects; the Worker has none. */
function isLegacyPayloadPath(path: string): boolean {
  return path === "/payload.json" || path.endsWith("/payload.json");
}

/**
 * Cache tags that clear the Worker's copies of one www page, or `[]` when the
 * Worker does not answer that path (a Fastly-only page).
 *
 * - a hub / listing page → its family tag (`asset-center:crypto`);
 * - an asset page or any of its tabs → the per-ticker tag `asset:<class>:<ticker>`,
 *   which every tab's response carries, so one tag covers every tab and every
 *   shell variant (`anon`, `auth-<plan>`);
 * - anything else under a Worker prefix → the whole class tag `asset:<class>`.
 *   Over-purging is safe; a silent no-op is not.
 *
 * The ticker keeps its colons (`asset:commodity:cm:xauusd`): a purge matches the
 * WHOLE tag string, never a segment.
 */
export function workerTagsForPath(rawPath: string): string[] {
  const path = normalizePath(rawPath);
  const family = workerFamilyForPath(path);
  if (!family) return [];
  if (isLegacyPayloadPath(path)) return [];

  const exact = family.pages[path];
  if (exact) return tagList(exact);

  const prefixes = Object.entries(family.pagePrefixes ?? {}).sort(
    (a, b) => b[0].length - a[0].length
  );
  for (const [prefix, tag] of prefixes) {
    if (path.startsWith(prefix)) return tagList(tag);
  }

  // A page family with no asset pages: anything under it clears its page tags.
  if (!family.assetClass || !family.assetBase) return [...new Set(Object.values(family.pages).flatMap(tagList))];

  const base = `${family.assetBase}/`;
  if (path.startsWith(base)) {
    const ticker = path.slice(base.length).split("/")[0];
    if (ticker) return [`asset:${family.assetClass}:${ticker}`];
  }

  return [`asset:${family.assetClass}`];
}

/** Asset-rebuild type key → the website's asset class (the tag segment). */
export const ASSET_CLASS_BY_TYPE = {
  etf: "etf",
  fund: "fund",
  coin: "crypto",
  index: "index",
  forex: "fx",
  commodity: "commodity",
  "private-company": "private-company",
} as const satisfies Record<AssetTypeKey, WorkerAssetClass>;

/** Same normalization as the URL builder: lower-case, no `fx:` prefix. */
export function normalizeAssetTicker(raw: string): string {
  return raw.trim().toLowerCase().replace(/^fx:/, "");
}

/**
 * Per-ticker tags for an asset rebuild. `asset:<class>:<ticker>` is on every
 * tab of the asset, so it clears the whole asset (private companies also carry
 * the `pc:` alias tag; passing the alias as the ticker matches that one).
 */
export function workerTagsForAsset(
  assetType: AssetTypeKey,
  tickers: readonly string[]
): string[] {
  const cls = ASSET_CLASS_BY_TYPE[assetType];
  const unique = new Set(
    tickers.map(normalizeAssetTicker).filter((t) => t.length > 0)
  );
  return [...unique].map((t) => `asset:${cls}:${t}`);
}

/** CDN(s) a single www page's purge reaches. Dual purge: Fastly is always kept. */
export function cdnsForPath(path: string): PurgeCdn[] {
  return workerTagsForPath(path).length > 0
    ? ["cloudflare-tags", "fastly-urls"]
    : ["fastly-urls"];
}

/** CDN(s) an asset purge reaches: the Worker tag and every tab on Fastly. */
export function cdnsForAssetPurge(): PurgeCdn[] {
  return ["cloudflare-tags", "fastly-urls"];
}

/** Operator-facing label for a CDN set: "Cloudflare tags", "Fastly URLs" or "both". */
export function describeCdns(cdns: readonly PurgeCdn[]): string {
  const has = (cdn: PurgeCdn) => cdns.includes(cdn);
  if (has("cloudflare-tags") && has("fastly-urls")) return "both (Cloudflare tags + Fastly URLs)";
  if (has("cloudflare-tags")) return "Cloudflare tags";
  if (has("cloudflare-urls")) return "Cloudflare URLs";
  if (has("fastly-urls")) return "Fastly URLs";
  return "none";
}
